import { useCallback, useState } from 'react';
import type { SurveyAnswers, SurveyOtherText, SurveyQuestion } from '../types/api';
import { Button, Modal, TextInput } from '../ui';
import styles from './OnboardingSurvey.module.css';

export interface OnboardingSurveyProps {
  questions: SurveyQuestion[];
  /** Title and opening paragraph, both from the backend registry. */
  title: string;
  intro: string;
  submitting: boolean;
  onSubmit: (answers: SurveyAnswers, otherText: SurveyOtherText) => void;
}

/** Matches `MAX_FREE_TEXT` in core/survey.py, which is the boundary that really holds. */
export const MAX_FREE_TEXT = 200;

/**
 * What ticking `key` leaves selected.
 *
 * Exclusivity is a property of the option rather than a known key, because the thing
 * that cannot be combined with anything differs per question: "None" among a list of
 * tools, "Prefer not to say" among anything. The backend says which, and this obeys.
 */
export function nextSelection(question: SurveyQuestion, current: string[], key: string): string[] {
  // A radio never fires a change for the option already chosen, so there is no
  // deselect case to handle here: picking one always replaces the other.
  if (question.select === 'single') return [key];

  const exclusive = new Set(
    question.options.filter((option) => option.exclusive).map((option) => option.key),
  );
  if (exclusive.has(key)) {
    return current.includes(key) ? [] : [key];
  }
  const without = current.filter((k) => k !== key && !exclusive.has(k));
  return current.includes(key) ? without : [...without, key];
}

/**
 * Whether a question has a usable answer.
 *
 * A chosen free-text option with an empty box does not count. Ticking "Other" and
 * typing nothing says less than leaving the question alone, and the person is about to
 * be let into the app on the strength of it.
 */
export function isAnswered(
  question: SurveyQuestion,
  selected: string[],
  otherText: SurveyOtherText,
): boolean {
  if (selected.length === 0) return false;
  const needsText = question.options.some(
    (option) => option.free_text && selected.includes(option.key),
  );
  return !needsText || (otherText[question.key] ?? '').trim().length > 0;
}

/** Whether every question has a usable answer. What decides who gets into the app. */
export function isComplete(
  questions: SurveyQuestion[],
  answers: SurveyAnswers,
  otherText: SurveyOtherText,
): boolean {
  return questions.every((q) => isAnswered(q, answers[q.key] ?? [], otherText));
}

/**
 * The onboarding survey, asked once, before anybody reaches the assistant.
 *
 * A locked dialog, so nobody reaches the assistant without passing through it. What
 * keeps that defensible is that nothing in it has to be answered: every question carries
 * a "Prefer not to say", which the backend guarantees rather than this component. The
 * dialog therefore costs a few clicks and never an answer somebody did not want to give.
 * It also says why it is asking, at the point of asking, and says the asking is only for
 * the trial. Every word of that comes from the backend registry rather than from here,
 * so the wording somebody consented to is reviewed in one place.
 *
 * One question per screen, with Back and Next. Next is disabled until the question on
 * screen is answered, which is what makes reaching the last screen mean everything
 * before it is answered, so Submit never has to explain a refusal three questions ago.
 *
 * Radios and checkboxes are native inputs rather than styled buttons, for the same
 * reason `Modal` uses `<dialog>`: arrow-key movement within a radio group, the group
 * announced as one thing, and label-click targeting all come free and are all fiddly
 * to rebuild. `select` decides which of the two a question gets.
 */
export default function OnboardingSurvey({
  questions,
  title,
  intro,
  submitting,
  onSubmit,
}: OnboardingSurveyProps) {
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [otherText, setOtherText] = useState<SurveyOtherText>({});
  const [index, setIndex] = useState(0);

  const toggle = useCallback((question: SurveyQuestion, key: string) => {
    setAnswers((current) => ({
      ...current,
      [question.key]: nextSelection(question, current[question.key] ?? [], key),
    }));
  }, []);

  const question = questions[index];
  const selected = question ? (answers[question.key] ?? []) : [];
  const isLast = index === questions.length - 1;

  return (
    <Modal
      open={questions.length > 0}
      dismissible={false}
      size="md"
      title={title}
      description={index === 0 ? intro : undefined}
      footer={
        <>
          {index > 0 && (
            <Button variant="ghost" onClick={() => setIndex(index - 1)} disabled={submitting}>
              Back
            </Button>
          )}
          {isLast ? (
            <Button
              variant="primary"
              loading={submitting}
              disabled={!isComplete(questions, answers, otherText)}
              onClick={() => onSubmit(answers, otherText)}
            >
              Submit
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!isAnswered(question, selected, otherText)}
              onClick={() => setIndex(index + 1)}
            >
              Next
            </Button>
          )}
        </>
      }
    >
      {question && (
        // The progress line sits outside the fieldset because a legend has to be the
        // fieldset's first child; putting anything above it inside breaks the pairing
        // that tells a screen reader which question these options belong to.
        <>
          <p className={styles.progress}>
            Question {index + 1} of {questions.length}
          </p>
          <fieldset className={styles.question}>
            <legend className={styles.legend}>{question.text}</legend>
            {question.hint && <p className={styles.hint}>{question.hint}</p>}
            <div className={styles.options}>
              {question.options.map((option) => (
                <div key={option.key}>
                  <label className={styles.option}>
                    <input
                      type={question.select === 'single' ? 'radio' : 'checkbox'}
                      name={question.key}
                      value={option.key}
                      checked={selected.includes(option.key)}
                      onChange={() => toggle(question, option.key)}
                      disabled={submitting}
                    />
                    <span>{option.label}</span>
                  </label>
                  {option.free_text && selected.includes(option.key) && (
                    <div className={styles.freeText}>
                      <TextInput
                        aria-label={`${option.label}: please specify`}
                        placeholder="Please specify"
                        maxLength={MAX_FREE_TEXT}
                        value={otherText[question.key] ?? ''}
                        disabled={submitting}
                        onChange={(e) =>
                          setOtherText((current) => ({
                            ...current,
                            [question.key]: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </fieldset>
        </>
      )}
    </Modal>
  );
}
